<script lang="ts">
  import RadioButton from '$components/ui/radio-button.svelte';
  import { escapeKey } from '$lib/actions/escape-key';
  import { focusTrap } from '$lib/actions/focus-trap';
  import { clickOutside } from '$lib/actions/click-outside';
  import { fade, fly } from 'svelte/transition';
  import { reducedMotion } from '$lib/stores/reduced-motion';
  import Icon from '$components/ui/icon/icon.svelte';
  import { cubicOut } from 'svelte/easing';
  import Button from '$components/ui/button.svelte';
  import { selectedGift } from './change-gift.svelte';
  import { t } from '$lib/i18n';
  import type {
    GetCartQuery,
    GetProductQuery,
    ProductCardFragment
  } from '$gql/storefront/operations';
  import { trimVariantId } from '$lib/utils/shopify/productsId';
  import { getVariantAlertId } from '$components/pdp/alerts';
  import { FREE_GIFT, ProductTags } from '$lib/constants';
  import { cart } from '$lib/cart';
  import { createEventDispatcher } from 'svelte';
  import { freeGiftCampaign } from '$lib/stores/campaigns';
  import { hasRichTextContent } from '$components/rich-text.svelte';
  import VariantsInformation from '$components/pdp/variants-information.svelte';
  import ShopifyPlans from '$components/pdp/variants/shopify-plans.svelte';

  export let product:
    | NonNullable<GetCartQuery['cart']>['lines']['nodes'][number]['merchandise']['product']
    | ProductCardFragment
    | undefined;
  export let loading = false;
  let productOptions: ProductCardFragment['options'] = [];
  let selectedProductVariant = $selectedGift?.variantId
    ? product?.variants?.nodes.find(
        (variant) => trimVariantId(variant.id) === $selectedGift?.variantId
      )
    : product?.variants?.nodes[0];

  const sellingPlans =
    product && product.sellingPlanGroups.edges.length > 0
      ? product.sellingPlanGroups.edges[0]?.node.sellingPlans.edges.map((e) => e.node)
      : [];

  $: selectedPlanId = sellingPlans.length > 0 ? sellingPlans[0]?.id || '' : undefined;

  $: freeProduct = $cart?.lines.nodes.find((line) =>
    (line.attributes || [])?.some((item) => item.key === FREE_GIFT)
  );
  $: alert = selectedVariant
    ? getVariantAlertId(selectedVariant, { isPreSale: false, wontBeRestocked: false })
    : undefined;

  $: isLimitedEdition = product?.tags?.includes(ProductTags.LIMITED);
  $: selectedProductVariantStock = selectedVariant?.quantityAvailable || 0;

  $: currentFreeGift = $freeGiftCampaign?.campaign_free_gifts
    ?.filter((p) => typeof p !== 'string')
    .find((gift) => gift.slug === product?.handle);

  const dispatch = createEventDispatcher<{ close: undefined; save: undefined }>();

  const resetSelected = () => {
    const result: Record<string, string> = {};

    if (selectedProductVariant) {
      product?.options?.forEach((option, i) => {
        const availableOptions = selectedProductVariant?.title.split(' / ') || [];
        result[option.name] = availableOptions[i];
      });
    } else {
      product?.options?.forEach((option) => {
        result[option.name] = option.values[0];
      });
    }

    return result;
  };

  let selectedOptions: Record<string, string> = resetSelected();

  $: selectedVariant = product?.variants?.nodes.find(
    (variant) => variant.title === Object.values(selectedOptions).join(' / ')
  ) as NonNullable<GetProductQuery['product']>['variants']['nodes'][number];

  $: {
    const availableVariants = product?.variants?.nodes.filter(
      (variant) => variant.availableForSale
    );

    const availableOptions = (availableVariants || []).map((variant) => {
      const titleParts = variant.title.split('/');

      return product?.options.reduce<Record<string, unknown>>((acc, option, index) => {
        acc[option.name as keyof typeof acc] = titleParts[index];
        return acc;
      }, {});
    });

    productOptions = (product?.options || []).map((option) => {
      return {
        name: option.name,
        values: option.values.filter((value) => {
          return availableOptions.some(
            (availableOption) => availableOption && availableOption[option.name] === value
          );
        })
      };
    });
  }

  $: if (product && product?.sellingPlanGroups.edges.length > 0) {
    productOptions = product?.options;
  }

  const onClose = () => {
    selectedGift.update((curr) => {
      return {
        ...curr,
        handle: freeProduct?.merchandise?.product?.handle as string,
        variantId: trimVariantId(freeProduct?.merchandise?.id as string),
        sellingPlanId: selectedPlanId
      };
    });
    dispatch('close');
  };

  const onSave = () => {
    selectedGift.update((curr) => {
      return {
        ...curr,
        variantId: trimVariantId(selectedVariant?.id as string),
        sellingPlanId: selectedPlanId
      };
    });
    dispatch('save');
  };
</script>

<div
  class="fixed lg:absolute bottom-0 left-0 z-50 w-full rounded-sm flex flex-col p-0 overflow-hidden"
  use:escapeKey={{ id: 'change-gift' + product?.id, callback: onClose }}
  use:focusTrap
>
  <div
    transition:fly={{
      duration: $reducedMotion ? 0 : 300,
      y: '100%',
      opacity: 100,
      easing: cubicOut
    }}
    use:clickOutside={onClose}
    class="bg-highlight shadow mt-auto w-full rounded-sm overflow-y-auto"
  >
    <div class="p-2">
      <div class="flex justify-between items-center py-0.5">
        <p class="text-md">
          {#if productOptions.length === 1}
            {$t('product-card.choose-variant-var', {
              variant: (product?.options || [])[0].name.toLowerCase()
            })}
          {:else}
            {$t('product-card.choose-variant')}
          {/if}
        </p>
        <button
          type="button"
          class="flex outline-none border-none focus-visible:ring rounded-xs motion-safe:transition-shadow"
          on:click={onClose}
        >
          <Icon icon="cross" size="md" />
        </button>
      </div>

      <div class="mt-8 flex flex-col gap-4">
        {#each productOptions as option, i}
          <div>
            {#if productOptions.length > 1}
              <p class="mb-2 text-sm text-primary">{option.name}</p>
            {/if}
            <form
              on:change={(e) => {
                e.preventDefault();
              }}
              class="flex flex-wrap gap-2"
            >
              {#each option.values as value}
                {@const currentVariant = product?.variants?.nodes.filter(
                  (v) => v.title === value
                )[0]}
                {@const qnt = currentVariant?.quantityAvailable}
                <RadioButton
                  {value}
                  variant="ghost"
                  disabled={isLimitedEdition && qnt === 0}
                  label={value}
                  id={option.name + value + 'add-to-cart'}
                  bind:group={selectedOptions[option.name]}
                />
              {/each}
            </form>

            {#if i === productOptions.length - 1}
              {#if alert}
                <p class="mt-2 text-sm/none text-primary/80 h-5">
                  <span
                    class="flex items-center gap-1"
                    transition:fade={{ duration: $reducedMotion ? 0 : 100 }}
                  >
                    <Icon size="md" icon="alert-hexagon-bold" />
                    {$t(alert, {
                      count: selectedProductVariantStock.toString()
                    })}
                  </span>
                </p>
              {/if}
            {/if}
          </div>
        {/each}
      </div>

      {#if sellingPlans.length > 0 && selectedPlanId && product}
        <ShopifyPlans {product} bind:selectedPlanId />
      {/if}

      {#if currentFreeGift && currentFreeGift.content.variants_information?.content && hasRichTextContent(currentFreeGift.content.variants_information)}
        <VariantsInformation
          selectedProductVariant={selectedVariant}
          doc={currentFreeGift.content.variants_information}
          class="mb-2 mt-4"
        />
      {/if}

      <Button type="button" class="w-full mt-2" size="sm" {loading} on:click={() => onSave()}>
        Save
      </Button>
    </div>
  </div>
</div>
